import React, { useState, useEffect } from 'react';
import { CircularProgress, Grid, TextField, Typography } from '@mui/material';
import { blueGrey } from '@mui/material/colors';
import stepIcon from '../../../assets/registration/casino-pin.png'
import {makeStyles} from '@mui/styles'
import ContainedButton from '../../../components/actions/ContainedButton';
import DisplayError from '../../../components/alerts/DisplayError';
import { onboarding_server } from '../../../utils/axios';
import { v4 as uuid } from 'uuid';
import { useLocation } from 'react-router';
import qs from 'query-string'
import { useAuthContext } from '../../../auth/useAuthContext';
import { sleep } from '../../../utils/general';
import { REGISTRATION_STEPS } from '../../../utils/enums';

/**
 * @module Layouts/Registration/views/CasinoPinValidation
 * @description This is a React component for validating a casino player's PIN, cardholder account,
 * and/or a dynamic set of challenge fields (e.g. Zip Code) returned by the OnBoarding API.
 */

/**
 *
 * @typedef CasinoPinValidation
 * @property {MUIStyles} classes - The styles for the component
 * @property {Boolean} loading - Indicates if initialization process is taking place
 * @property {Boolean} sending - Indicates if a process has started
 * @property {Object | String | Boolean} error - Variable that handles error, it can be a boolean, a string or an object
 * @property {Object} constraints - Constraints returned from OnBoarding API
 * @property {Number} pin - Pin Number from SMS
 * @property {Array} challengeFields - Challenge field definitions (id, text, regex, minLength, maxLength, order)
 * @property {Boolean} valid - This flag indicates if the PIN is valid
 * @property {String} playerID - Casino Player ID from Query Parameters in URL (optional)
 * @property {Boolean} busy - Indicates if the current view is processing something
 * @returns The component `CasinoPinValidation` is being returned.
 */

const CasinoPinValidation = ({form, actions, system, data}) => {

    const classes = useStyles()

    const {login} = useAuthContext()

    const location = useLocation()

    const [loading, setLoading] = useState()
    const [sending, setSending] = useState()
    const [error, setError] = useState()
    const [constraints, setConstraints] = useState({})
    const [pin, setPin] = useState('')
    const [valid, setValid] = useState(false)
    const [visited, setVisited] = useState(false)
    const [playerID, setPlayerID] = useState(null)
    const [busy, setBusy] = useState(false)

    // Campos de challenge (definición del backend) y su estado de captura/validación.
    const [challengeFields, setChallengeFields] = useState([])
    const [challengeValues, setChallengeValues] = useState({})
    const [challengeValid, setChallengeValid] = useState({})
    const [challengeVisited, setChallengeVisited] = useState({})

    /**
     * @function initiliaze
     * @description Inicializa la vista: obtiene la info del cardholder, fija las constraints y prepara
     * los campos de challenge. Si el programa solo requiere validación de cuenta (sin PIN ni challenge),
     * valida automáticamente usando el playerId. Si requiere PIN y/o challenge, muestra el formulario.
     */
    const initiliaze = async() => {
        setLoading(true)
        try {
            setBusy(true)
            const args = qs.parse(location.search)
            if(args?.playerId){
                setPlayerID(args.playerId)
            }

            const cardholderinfo = await actions.onGetCardholerInfo()
            const _constraints = cardholderinfo?.enrollment?.constraints ?? {}
            setPin('')
            setError(null)
            setConstraints(_constraints)

            // Prepara los campos de challenge (si el programa los exige), ordenados por 'order'.
            const _challenge = _constraints?.challengeRequired ? [...(_constraints?.challengeFields ?? [])] : []
            _challenge.sort((a, b) => (a.order ?? 0) - (b.order ?? 0))
            setChallengeFields(_challenge)
            const initVals = {}, initValid = {}, initVisited = {}
            _challenge.forEach(f => { initVals[f.id] = ''; initValid[f.id] = false; initVisited[f.id] = false })
            setChallengeValues(initVals)
            setChallengeValid(initValid)
            setChallengeVisited(initVisited)

            // Solo se valida en automático cuando NO hay PIN ni challenge que pedir al usuario.
            if(!_constraints?.pinValidationRequired && !_constraints?.challengeRequired && _constraints?.accountValidationRequired){
                let data2send = {
                    transactionUuid: uuid(),
                }
                if(args.playerId){
                    data2send.playerId = args.playerId
                }else{
                    setError('Player Id is required')
                    setLoading(false)
                    return
                }
                const _request = await onboarding_server.post('/cardholder/validate', data2send)
                await login(_request?.data?.token)
                await sleep(500)
                const nextPath = await onEvaluateCardholder()
                actions.onUpdateView(nextPath)
                setLoading(false)
                setBusy(true)
                return
            }

        } catch (error) {
            console.log(error)
            if(error?.code === 401){
                await actions.onResetForm()
                return
            }
            setError(error?.message)
        }
        setBusy(true)
        setLoading(false)
    }

    useEffect(() => {
        if(!busy) initiliaze()
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    /**
     * @function onEvaluateCardholder
     * @description Tras validar, decide el siguiente paso según si ya está enrolado o si aceptó los
     * acuerdos. Devuelve un valor de REGISTRATION_STEPS.
     */
    const onEvaluateCardholder = async() => {
        let path = REGISTRATION_STEPS.TERMS_CONDITION
        try {
            const info = await actions.onRequestUserData()
            if(info?.enrollment?.enrolled){
                path = REGISTRATION_STEPS.SUCCESS
            }
            if(info?.agreement?.accepted){
                path = REGISTRATION_STEPS.USER_FORM // MUST BE USER FORM
            }
        } catch (error) {
            console.log(error)
        }
        return path
    }

    /**
     * @function onChange
     * @description Captura el PIN y valida contra el regex de las constraints.
     */
    const onChange = (e) => {
        const _value = e.target?.value
        setPin(_value)
        setVisited(true)
        if(constraints?.pinValidationRegex){
            const regex = new RegExp(constraints?.pinValidationRegex)
            setValid(regex.test(_value))
        }
    }

    /**
     * @function onChallengeChange
     * @description Captura el valor de un campo de challenge y lo valida contra su regex y longitudes.
     * @param field - Definición del campo (id, regex, minLength, maxLength)
     * @param rawValue - Valor capturado
     */
    const onChallengeChange = (field, rawValue) => {
        const value = rawValue ?? ''
        setChallengeValues(prev => ({ ...prev, [field.id]: value }))
        setChallengeVisited(prev => ({ ...prev, [field.id]: true }))
        let ok = true
        if(field.regex){
            try { ok = new RegExp(field.regex).test(value) } catch (e) { ok = true }
        }
        if(field.minLength != null && value.length < field.minLength) ok = false
        if(field.maxLength != null && value.length > field.maxLength) ok = false
        setChallengeValid(prev => ({ ...prev, [field.id]: ok }))
        if(error) setError(null)
    }

    /**
     * @function onSubmit
     * @description Envía la validación a /cardholder/validate incluyendo, según el programa, el PIN y/o
     * las respuestas de challenge, luego evalúa el siguiente paso.
     */
    const onSubmit = async() => {
        setSending(true)
        try {
            let data2send = { transactionUuid: uuid() }
            if(playerID) data2send.playerId = playerID
            if(constraints?.pinValidationRequired) data2send.pin = pin
            if(constraints?.challengeRequired){
                data2send.challengeFields = challengeFields.map(f => ({
                    id: f.id,
                    value: challengeValues[f.id] ?? ''
                }))
            }

            const _request = await onboarding_server.post('/cardholder/validate', data2send)
            await login(_request?.data?.token)
            await sleep(500)
            const nextPath = await onEvaluateCardholder()
            actions.onUpdateView(nextPath)

        } catch (error) {
            console.log(error)
            setSending(false)
            setError(error?.message)
            return
        }
        setSending(false)
    }

    const pinValidationRequired = constraints?.pinValidationRequired
    const challengeRequired = constraints?.challengeRequired

    const _error = visited && !valid

    // El botón se habilita cuando todo lo requerido es válido: PIN (si aplica) y todos los challenge (si aplica).
    const pinOk = !pinValidationRequired || valid
    const challengeOk = !challengeRequired || (challengeFields.length > 0 && challengeFields.every(f => challengeValid[f.id]))
    const formValid = pinOk && challengeOk

    return (
        <div className={classes.root}>
            <Grid container spacing={3} >
                <Grid item xs={12}>
                    <div className={classes.image_container}>
                        <img src={stepIcon} alt='' width='100%'/>
                    </div>
                </Grid>
                <Grid item xs={12}>
                    { loading && <div className={classes.loading_container}>
                        <div className={classes.loading}>
                            <CircularProgress size={64} />
                        </div>
                    </div> }
                    { !loading && <Grid container spacing={3}>
                        <Grid item xs={12}>
                            <Typography variant='h6' align='center' className={classes.title}>
                                {pinValidationRequired ? 'PIN Validation' : 'Cardholder Validation'}
                            </Typography>
                            {pinValidationRequired && <Typography variant='body1' align='center' className={classes.text}>
                                Please enter your Player's Club PIN, if you don't have it with you, please contact your Casino
                            </Typography>}
                            {!pinValidationRequired && challengeRequired && constraints?.challengeText &&
                                <Typography variant='body1' align='center' className={classes.text}>
                                    {constraints.challengeText}
                                </Typography>}
                        </Grid>
                        {pinValidationRequired && <Grid item xs={12}>
                            <Grid container justifyContent='center'>
                                <Grid item >
                                    <div style={{display:'inline-block', margin:'auto'}}>
                                        <TextField
                                            variant='outlined'
                                            defaultValue=''
                                            value={pin}
                                            inputProps={{
                                                style:{textAlign:'center', letterSpacing:3}
                                            }}
                                            error={_error}
                                            helperText={_error ? 'Invalid pin' : ''}
                                            onChange={onChange}
                                        />
                                    </div>
                                </Grid>
                            </Grid>
                        </Grid>}
                        {challengeRequired && challengeFields.map((field) => {
                            const val = challengeValues[field.id] ?? ''
                            const isErr = challengeVisited[field.id] && !challengeValid[field.id]
                            return (
                                <Grid item xs={12} key={field.id}>
                                    <TextField
                                        fullWidth
                                        variant='outlined'
                                        label={field.text || field.name || 'Value'}
                                        value={val}
                                        error={isErr}
                                        helperText={isErr ? `Invalid ${field.name || 'value'}` : ''}
                                        inputProps={{ maxLength: field.maxLength ?? undefined }}
                                        onChange={(e) => onChallengeChange(field, e.target.value)}
                                    />
                                </Grid>
                            )
                        })}
                        <Grid item xs={12}>
                            <DisplayError>{error}</DisplayError>
                            <ContainedButton loading={sending} fullWidth color='primary'
                                disabled={!formValid}
                                onClick={onSubmit}
                            >Continue</ContainedButton>
                        </Grid>
                    </Grid> }
                </Grid>

            </Grid>
        </div>
     );
}

const useStyles = makeStyles(theme => ({
    root:{
        width:'100%',
        maxWidth:400,
        margin:'auto',
        padding:'40px 0px'
    },
    image_container:{
        width:200,
        margin:'auto',
        [theme.breakpoints.down('sm')]:{
            width:150,
        }
    },
    title:{
        fontWeight:600,
        lineHeight:1.5,
    },
    text:{
        fontWeight:500,
        color:blueGrey[400],
        [theme.breakpoints.down('sm')]:{
            fontSize:'0.875rem'
        }
    },
    button:{
        color:theme.palette.primary.dark,
        textTransform:'uppercase'
    },
    loading_container:{
        width:200, height:200,
        margin:'auto',
        position:'relative'
    },
    loading:{
        position:'absolute',
        top:'50%', left:'50%',
        transform:'translate(-50%, -50%)'
    }
}))

export default CasinoPinValidation;
